import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ProductQuota } from '../lib/types'
import { agentsBalanceForecast, burnStepTo } from '../lib/forecast'
import { Card, CardBody, CardHeader } from './ui/Card'
import { QtyStepper } from './ui/QtyStepper'
import { SimulationBadge } from './ui/SimulationBadge'
import { VolumeChart, type ChartSeries } from './VolumeChart'

/** The Mambu Agents "Credits Balance" card on Subscription (MS-820 Q13;
 * prototype `#agt-balance-card` / `renderAgtBalance` ← `renderStatus('agt')`):
 * credits deplete rather than reset, so the chart is a runway — today's real
 * balance, the projected balance at the start of each month at the
 * consumption entered below, and the annual allowance credited again on the
 * contract anniversary as its own segment.
 *
 * The Simulation badge sits in the header, left of the collapse toggle; the
 * "Projected Credits / Month" control sits below the chart (§24e-sim-pos),
 * seeded from the account's own trailing average (Q15) and stepping on the
 * 100,000 ladder. The first month is prorated (Q20), as in the purchase
 * simulator. */
export function AgentsCreditsBalanceCard({
  quota,
  contractStart,
  contractEnd,
}: Readonly<{ quota: ProductQuota; contractStart: string | null; contractEnd: string | null }>) {
  const { t } = useTranslation()
  const [burn, setBurn] = useState(quota.avgMonthlyConsumption ?? 0)
  useEffect(() => setBurn(quota.avgMonthlyConsumption ?? 0), [quota.avgMonthlyConsumption])

  const months = useMemo(
    () =>
      agentsBalanceForecast(
        quota.balanceRemaining ?? 0,
        quota.includedVolume,
        contractStart,
        contractEnd,
        burn,
        new Date()
      ),
    [quota.balanceRemaining, quota.includedVolume, contractStart, contractEnd, burn]
  )
  const series: ChartSeries[] = [
    { key: 'starting', label: t('subscription.creditsBalance.balanceToday'), tone: 'starting' },
    { key: 'base', label: t('subscription.creditsBalance.projectedStart'), tone: 'projected' },
    { key: 'credited', label: t('subscription.creditsBalance.annualCredited'), tone: 'contracted' },
  ]

  return (
    <Card collapsible>
      <CardHeader
        title={t('subscription.creditsBalance.title')}
        subtitle={t('subscription.creditsBalance.subtitle')}
        actions={<SimulationBadge />}
      />
      <CardBody>
        <VolumeChart
          months={months}
          series={series}
          legendTitle={t('subscription.creditsBalance.legendTitle')}
          yTitle={t('subscription.creditsBalance.axisTitle')}
          totalLabel={() => t('subscription.creditsBalance.total')}
        />
        <div className="flex justify-end mt-3">
          <ProjectedControl label={t('subscription.creditsBalance.projected')} value={burn} onChange={setBurn} />
        </div>
      </CardBody>
    </Card>
  )
}

/** The prototype's `.sim-input`: the projected-consumption control, in the
 * info blue — an editable assumption, not a figure the account holds. */
export function ProjectedControl({
  label,
  value,
  onChange,
}: Readonly<{ label: string; value: number; onChange: (next: number) => void }>) {
  return (
    // Not a <label>: a label's control is its first labelable descendant, and
    // here that is the "−" button — clicking the text would decrement the
    // projection, and the input would have no accessible name. The input is
    // named through QtyStepper's `label` instead.
    <div className="inline-flex items-center gap-2.5 text-[11.5px] font-semibold text-[var(--info)] bg-[var(--info-subtle)] border border-[var(--info-border)] rounded-lg py-[5px] pr-1.5 pl-[11px]">
      {label}
      <QtyStepper value={value} onChange={onChange} min={0} editable stepTo={burnStepTo} tone="info" label={label} />
    </div>
  )
}
